import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Collapse, Grid } from "@mui/material";
import { grey } from "@mui/material/colors";
import moment from "moment";
import React, { useState } from "react";
import { useCurrencyLocalizer } from "../../hooks/useCurrencyLocalizer";
import { createKey } from "../../utils/createKey";
import { MealDaySummary } from "./MealDaySummary";
import { CollapsibleOrderSummaryLine } from "./orderSummarySubComponents";
import { ProductSummary } from "./ProductSummary";
/**
 * Renders a summary of meals for a specific day.
 * It provides a collapsible view of the meals, along with pricing information.
 *
 * @component
 * @param {Object} props.day - An object representing the meal day's details.
 * @param {string} props.day.date - The date for the meal day.
 * @param {MealSummary[]} props.day.meals - An array of meal summaries for the day.
 * @param {CartVisibilitySettings} props.settings - Settings controlling visibility of prices and other display options.
 * @param {number} props.dayPrice - The total price for the day.
 * @param {boolean} props.canAddRemoveMeals - A flag indicating whether meals can be added or removed.
 * @param {boolean} props.canAddRemoveRecipes - A flag indicating whether recipes can be added or removed.
 * @param {boolean} [props.isInvoiceModal] - An optional flag indicating if the summary is displayed in an invoice modal.
 *
 */
export const CollapsibleMealDaySummary = ({ day, settings, dayPrice, canAddRemoveMeals, canAddRemoveRecipes, isInvoiceModal, loading = false, balance }) => {
    var _a, _b, _c;
    const [open, setOpen] = useState(settings.isDayDefaultExpanded);
    const { localizedCurrencyText } = useCurrencyLocalizer(dayPrice);
    /**
     * Toggles the `open` state between true and false, which controls the visibility of the collapsible content.
     */
    const handleChange = () => {
        setOpen((prev) => !prev);
    };
    const totalProductQuantity = (_a = day.products) === null || _a === void 0 ? void 0 : _a.reduce((acc, product) => acc + product.quantity, 0);
    /*
      Gets the value discounted from a meal by per meal discounts
      Per meal discounts may only apply to certain meal type ids, so we must check the discount value for each program meal
    */
    const getDiscountPerMeal = (mealSummary) => {
        var _a, _b, _c;
        if (!settings.discountPerMeal.display) {
            return undefined;
        }
        let flattenedDayGroups = (_c = (_b = (_a = balance === null || balance === void 0 ? void 0 : balance.cost) === null || _a === void 0 ? void 0 : _a.order) === null || _b === void 0 ? void 0 : _b.distributions) === null || _c === void 0 ? void 0 : _c.flatMap((d) => {
            return d.dayGroups;
        });
        if (flattenedDayGroups == undefined) {
            return undefined;
        }
        const daygroup = flattenedDayGroups.find(d => moment(d.day).format("YYYY-MM-DD") == moment(day.date).format("YYYY-MM-DD"));
        if (daygroup == undefined) {
            return undefined;
        }
        const meal = daygroup.meals.find(m => m.programMealId === (mealSummary === null || mealSummary === void 0 ? void 0 : mealSummary.programMealId));
        if (meal == undefined) {
            return undefined;
        }
        return meal.discount;
    };
    return (_jsxs(React.Fragment, { children: [_jsx(Grid, Object.assign({ item: true, xx: 12, sx: { pt: 1 } }, { children: _jsx(CollapsibleOrderSummaryLine, { onChange: handleChange, open: open, label: moment(day.date).format("dddd MMM Do"), value: settings.dayPrices.display && !settings.hidePrices ? localizedCurrencyText : undefined, loading: loading }) })), _jsxs(Collapse, Object.assign({ in: open, unmountOnExit: true, sx: { backgroundColor: grey[100], padding: 1.5, width: "100%" } }, { children: [(_b = day.meals) === null || _b === void 0 ? void 0 : _b.map((m, i) => (_jsx(MealDaySummary, { date: day.date, meal: m, displaySelection: settings.displayOfQuantitySelectedText.display, displayMealPrices: settings.mealPrices.display, displayRecipes: settings.recipe.display, unselectedText: settings.recipe.unselectedText, discountPerMeal: getDiscountPerMeal(m), hidePrices: settings.hidePrices, canAddRemoveMeals: canAddRemoveMeals, canAddRemoveRecipes: canAddRemoveRecipes, isInvoiceModal: isInvoiceModal, recipeDisplayNameType: settings.recipeDisplayNameType, isDayPastCutoff: day.isDayPastCutoff }, createKey()))), ((_c = day.products) === null || _c === void 0 ? void 0 : _c.length) ? (_jsx(ProductSummary, { products: day.products, hidePrices: settings.hidePrices, productLabel: `${totalProductQuantity} ${settings.productSectionHeading}` })) : null] }))] }));
};
